/**
 * Pure feature-flag evaluator. No IO, no globals — every input is
 * passed in explicitly so the same function works in the browser and
 * on the API.
 *
 * @agentic @benchmark/core
 */ /**
 * Trim and lowercase a user email. Returns `null` for `undefined`,
 * `null`, empty, or whitespace-only input so the evaluator can short-
 * circuit cleanly.
 * @agentic @benchmark/core
 */ const normalizeEmail = (email)=>{
    if (!email) return null;
    const trimmed = email.trim().toLowerCase();
    return trimmed.length > 0 ? trimmed : null;
};
/**
 * Evaluate a single rule against a context.
 *
 * Precedence:
 *   1. `ctx.override` (env-var driven). If `true` or `false`, return
 *      it directly — the override beats every rule predicate. This is
 *      the emergency kill-switch / force-on lever.
 *   2. The three-predicate `FlagRule` disjunction (env / email /
 *      per-env email).
 *
 * @agentic @benchmark/core
 */ const evaluateFlag = (rule, ctx)=>{
    if (ctx.override !== undefined) return ctx.override;
    if (rule.environments?.has(ctx.environment)) return true;
    const email = normalizeEmail(ctx.email);
    if (email === null) return false;
    if (rule.emails?.has(email)) return true;
    return rule.emailsByEnvironment?.[ctx.environment]?.has(email) ?? false;
};

/**
 * Env-var override helpers for the feature-flag system. Pure: no
 * `process.env` or `import.meta.env` access here — each consuming app
 * reads its own env (Node vs. Vite) and passes the raw string in.
 *
 * @agentic @benchmark/core
 */ /**
 * Parse a raw env-var string into a flag-override decision.
 *
 * Accepts exactly `"true"` (forces the flag on) or `"false"` (forces
 * it off), case- and surrounding-whitespace-insensitive. Any other
 * input — `undefined`, empty string, `"1"`, `"yes"`, `"True!"` —
 * returns `undefined`, meaning "no override; defer to the rule."
 *
 * Strict parsing is intentional: an operator who types `yes` in the
 * GitHub Actions UI should not accidentally flip a flag on. The
 * conservative default is "rule wins."
 *
 * @agentic @benchmark/core
 */ const parseFlagOverride = (value)=>{
    if (value === undefined) return undefined;
    const trimmed = value.trim().toLowerCase();
    if (trimmed === "true") return true;
    if (trimmed === "false") return false;
    return undefined;
};
/**
 * Convert a camelCase flag name to the canonical env-var suffix used
 * by both apps. `inactivityLogout` → `"FEATURE_INACTIVITY_LOGOUT"`.
 *
 * - The API reads `process.env[<suffix>]` directly.
 * - Connections reads `import.meta.env["VITE_" + <suffix>]` (Vite
 *   requires the `VITE_` prefix to expose vars to the browser bundle).
 *
 * @agentic @benchmark/core
 */ const flagEnvVarSuffix = (name)=>{
    const snake = name.replace(/([A-Z])/g, "_$1").toUpperCase();
    return `FEATURE_${snake}`;
};

/**
 * The canonical flag map. Authoring this `as const satisfies` gives
 * downstream callers strong autocomplete (`FlagName` below) while
 * still type-checking every rule against `FlagRule`.
 * @agentic @benchmark/core
 */ const FEATURE_FLAGS = {
    inactivityLogout: {
        // Enforce inactivity-based auto-logout in deployed environments only.
        // Local dev keeps it off so engineers don't lose 50-minute sessions
        // mid-debug. Operators can flip behaviour via
        // `VITE_FEATURE_INACTIVITY_LOGOUT="true" | "false"` at redeploy time.
        environments: new Set([
            "staging",
            "production"
        ])
    },
    dialpadCareTimeAutoCreate: {
        // Whether the Dialpad webhook auto-creates a care-time Observation per
        // call/SMS. Disabled in deployed environments: call minutes are surfaced
        // as a manual prefill instead of generating parallel entries that get
        // double-counted against the clinician's manual log. Left on in
        // development/test so the tee path and its tests still exercise creation,
        // and so the future "auto-track + bill call time" effort can re-enable it.
        // Flip per deploy via `FEATURE_DIALPAD_CARE_TIME_AUTO_CREATE="true"`.
        environments: new Set([
            "development",
            "test"
        ])
    },
    bandwidthDiagnostics: {
        // In-browser network throughput diagnostics for ICT staff. Gated in the
        // UI by PractitionerRole code `ict`; this flag is the per-environment
        // kill switch. Flip via `VITE_FEATURE_BANDWIDTH_DIAGNOSTICS="true" |
        // "false"` at redeploy time.
        environments: new Set([
            "development",
            "staging",
            "production"
        ])
    },
    dialpadCallSelector: {
        // Gates the manual "select the Dialpad call you're documenting" picker
        // (`DialpadCallPicker`) wherever care time is captured — intake care time,
        // the non-intake encounter forms, and the care-time quick-add modal.
        //
        // Held OFF in production so the care-time double-dipping hotfix (its sibling
        // `dialpadCareTimeAutoCreate` + the server-side reconciliation in
        // `upsertEncounter`) can deploy to production without also shipping the
        // still-in-validation manual-call-selection UX they were merged alongside.
        // The hotfix does NOT depend on this picker: hiding it degrades cleanly to
        // manual-minutes entry (the pre-enhancement behaviour). On in
        // development/test/staging where the UX is being exercised. Flip per deploy
        // via `VITE_FEATURE_DIALPAD_CALL_SELECTOR="true" | "false"`.
        environments: new Set([
            "development",
            "test",
            "staging"
        ])
    },
    demoMode: {
        // Presenter-facing "demo mode" toggle in the Connections sidebar footer.
        // Hides the internal-only nav entries (Library, Diagnostics) so a clinician
        // audience sees clinician chrome. This flag is the per-environment kill
        // switch; the UI additionally gates on PractitionerRole code `ict` (see
        // `useDemoMode`). Mirrors `bandwidthDiagnostics` (env-wide flag +
        // client-side role gate). Off in `test` so component tests opt in
        // explicitly. Flip via `VITE_FEATURE_DEMO_MODE="true" | "false"` at
        // redeploy time.
        environments: new Set([
            "development",
            "staging",
            "production"
        ])
    }
};
/**
 * Look up a flag by name and evaluate it against the given context.
 * @agentic @benchmark/core
 */ const isFeatureEnabled = (name, ctx)=>evaluateFlag(FEATURE_FLAGS[name], ctx);

export { FEATURE_FLAGS, evaluateFlag, flagEnvVarSuffix, isFeatureEnabled, normalizeEmail, parseFlagOverride };
